import { Component, Injector, OnInit } from '@angular/core';
import { fadeAnimation } from 'animations';
import {
  ActivationMethod,
  ActivationMethodEnum,
  AvailableActivationMethods,
  AvailableOperator,
  AvailableOperators,
  Cart,
  DynamicEntityKey,
  Price,
  StaticEntityKey,
  TotalPriceEnum
} from 'models';
import { debounceTime, filter, first, pairwise, withLatestFrom } from 'rxjs/operators';
import { ExecService } from 'services';
import { ActivationMethodTypes } from '../../../constants/activation.constants';
import { ProcessStepBaseComponent } from '../../_core/process-step-base.component';


@Component({
  selector: 'sb-activation',
  templateUrl: './activation.component.html',
  styleUrls: ['./activation.component.scss'],
  animations: [ fadeAnimation ]
})
export class ActivationComponent extends ProcessStepBaseComponent implements OnInit {
  public MnpOperators: AvailableOperator[] = [];
  public isMnp: boolean;
  public shouldShowActivationMethods: boolean;
  public shouldShowIccidModal: boolean;
  public showTutorial: boolean;

  private unlistedOperator: AvailableOperator =  {
    id: 'NOTLISTED',
    name: '- Altro operatore',
    donatingCode: '00'
  };

  public availableActivationMethods: ActivationMethodEnum[] = [];
  public activationMethods = ActivationMethodTypes;

  constructor(injector: Injector) {
    super(injector);
  }

  ngOnInit() {
    // super.shouldShowBackButton();
    super.isStickyNavigation();
    super.setFormGroup(this.formsService.generateStepActivationForm());
    super.setFormStoreDataFunction(this.appStoreFacadeService.step.storeStepActivationValue);
    this.getFormDataAndValidation();
    this.setupSubscriptions();
  }

  public goToNextStep() {
    const activationMethodValue = this.formGroup.get('activationMethod.method').value;
    if (activationMethodValue !== ActivationMethodEnum.Mnp) {
      this.appStoreFacadeService.entity.storeEntityValue(DynamicEntityKey.ActivationMnpInfo,
        this.formGroup.get('activationMnpInfo').value);
    }
    window.localStorage.setItem('carrier-origin', this.formGroup.get('activationMnpInfo.operator').value);
    super.goToNextStep();
  }

  get shouldShowOperatorMessage(): boolean {
    return this.formGroup.get('activationMnpInfo.operator').value === this.unlistedOperator.id;
  }

  private getFormDataAndValidation() {
    this.subscriptions.add(
      this.appStoreFacadeService.entity.availableOperators$
        .pipe(first(Boolean))
        .subscribe((availableOperators: AvailableOperators) => {
          const sortedOperators = availableOperators.operators.slice().sort((a, b) => {
            if (a.name.toLowerCase() > b.name.toLowerCase()) {
              return 1;
            }
            if (a.name.toLowerCase() < b.name.toLowerCase()) {
              return -1;
            }
            return 0;
          });
          this.MnpOperators = [ ...sortedOperators, this.unlistedOperator ];
        })
    );

    this.subscriptions.add(
      this.appStoreFacadeService.step.activationValue$
        .pipe(debounceTime(150), first(Boolean))
        .subscribe(() => {
          this.sendAnalyticsData();
        })
    );

    this.subscriptions.add(
      this.formGroup.get('activationMnpInfo.operator').valueChanges
        .pipe(filter(value => value === this.unlistedOperator.id))
        .subscribe((value) => {
          window.localStorage.removeItem('carrier-origin');
          this.analyticsService.sendOperatorError(this.MnpOperators.map(op => op.name));
        })
    );

    this.subscriptions.add(
      this.formGroup.get('activationMnpInfo.operator').valueChanges
        .pipe(
          pairwise(),
          filter(([prev, next]) => next !== this.unlistedOperator.id),
          withLatestFrom(this.appStoreFacadeService.cart.cart$)
        ).subscribe(([[prev, next], cart]) => {
          this.manageOperatorChange(next, cart, prev);
          // Do another check for  ICCID validity
          this.formGroup.get('activationMnpInfo.iccid').updateValueAndValidity({ onlySelf: true });
      })
    );

    this.subscriptions.add(
      this.appStoreFacadeService.entity.availableActivationMethods$
        .pipe(first(Boolean))
        .subscribe(({ methods }: AvailableActivationMethods) => {
          this.availableActivationMethods = methods;
          this.activationMethods = this.availableActivationMethods.map(method => {
            return ActivationMethodTypes.find((el) => el.method === method);
          });
          this.shouldShowActivationMethods = this.activationMethods.length > 1;
        })
    );

    this.subscriptions.add(
      this.appStoreFacadeService.step.activation$
        .pipe(
          withLatestFrom(this.appStoreFacadeService.entity.availableActivationMethods$),
          first((args) => args.every(Boolean)),
        ).subscribe( ([ stepData ]) => {
          super.setFormGroupStepData(stepData);
          this.setMnpValueAndForm();
      })
    );
  }

  private manageOperatorChange(operatorId: string, cart: Cart, previousOperatorId: string) {
    if (operatorId) {
      const operatorInfo = this.MnpOperators.find(op => op.id === operatorId);
      if (operatorInfo && operatorInfo.configuration && operatorInfo.configuration.activationPrice) {
        const newCart = this.getUpdatedCart(cart, operatorInfo.configuration.activationPrice, TotalPriceEnum.Activation);
        if (newCart.hasChanged) {
          const pageName = `/popup/costo-attivazione-da-${newCart.oldPriceBeforeChange}-a-${newCart.newPriceAfterChange}`;
          this.analyticsService.sendCustomPage(pageName, {
            activation_price_new: newCart.newPriceAfterChange,
            activation_price_old: newCart.oldPriceBeforeChange
          });
          this.appStoreFacadeService.errors.openOperatorPriceChangeModal(
            {
              newPrice: newCart.newPriceAfterChange,
              oldPrice: newCart.oldPriceBeforeChange,
              currency: operatorInfo.configuration.activationPrice.currency
            },
            (proceed) => {
              if (proceed) {
                this.analyticsService.sendCustomEvent({
                  custom_category: 'popup costo attivazione',
                  custom_action: 'procedi',
                  custom_label: pageName
                });
                this.execService.verifyOperatorUpdate(operatorId)
                  .subscribe(updatedCart => {
                    this.appStoreFacadeService.entity.storeEntityValue(StaticEntityKey.Cart, updatedCart.cart);
                  });
              } else {
                this.analyticsService.sendCustomEvent({
                  custom_category: 'popup costo attivazione',
                  custom_action: 'chiudi',
                  custom_label: pageName
                });
                this.formGroup.patchValue({
                  activationMnpInfo: {
                    operator: previousOperatorId || null
                  }
                });
              }
            }
          );
        }
      }
    }
  }

  private getUpdatedCart(cart: Cart, newPrice: Price, priceType: TotalPriceEnum) {
    let oldPriceBeforeChange = 0;
    let newPriceAfterChange = 0;
    let hasChanged = false;
    const simPrice = cart.totalPrices.find(c => c.type === TotalPriceEnum.Sim).price.value || 0;
    const newTotalPrices = {...cart}.totalPrices.map(p => {
      const p2 = {...p};
      if (p2.type === priceType && p2.price.value !== newPrice.value) {
        hasChanged = true;
        oldPriceBeforeChange = (p2.price.value || 0) + simPrice;
        p2.price = newPrice;
        newPriceAfterChange = (newPrice.value || 0) + simPrice;
      }
      return p2;
    });
    const newCart = {
      ...cart,
      totalPrices: newTotalPrices,
      totalPrice: {
        ...cart.totalPrice,
        price: {
          ...cart.totalPrice.price,
          value: (cart.totalPrice.price.value || 0) - oldPriceBeforeChange + newPriceAfterChange
        }
      }
    };
    return {
      hasChanged,
      oldPriceBeforeChange,
      newPriceAfterChange,
      cart: newCart
    };
  }

  private setMnpValueAndForm() {
    const activationMethodValue = this.formGroup.get('activationMethod.method').value;
    // If there is only one method available and no method set, set the method to that one.
    if (this.availableActivationMethods.length === 1 && !activationMethodValue) {
      this.formGroup.get('activationMethod').patchValue({
        method: this.availableActivationMethods[0]
      });
    } else {
      // Commented because Business told us not to do it
      /*if (activationMethodValue == null) {
        this.formGroup.get('activationMethod').patchValue({
          method: ActivationMethodEnum.New
        });
      }*/
    }
    this.toggleMnpInfoForm();
    this.isMnp =  activationMethodValue === ActivationMethodEnum.Mnp;
  }

  private setupSubscriptions() {
    const activationMethodFormGroup = this.formGroup.get('activationMethod');
    this.subscriptions.add(
      activationMethodFormGroup.valueChanges
        .subscribe((activationMethod: ActivationMethod) => {
          this.setMnpValueAndForm();
        })
    );
  }

  private toggleMnpInfoForm() {
    const isMnp = this.formGroup.get('activationMethod.method').value === ActivationMethodEnum.Mnp;
    if (isMnp) {
      this.formGroup.get('activationMnpInfo').enable();
    } else {
      this.formGroup.get('activationMnpInfo').disable();
      this.formGroup.get('activationMnpInfo').reset();
    }
  }

  public onServiceConditionClick() {
    this.analyticsService.sendBrandClick();
  }

  public openModal() {
    this.shouldShowIccidModal = true;
    this.analyticsService.sendCustomPage('/popup/iccid');
  }

  public closeModal() {
    this.shouldShowIccidModal = false;
  }

  toggleVRTutorial() {
    this.showTutorial = !this.showTutorial;
  }

}
